import { ErrorHandler, LOCALE_ID, NgModule, inject, provideAppInitializer } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { LocationStrategy, PathLocationStrategy, registerLocaleData } from '@angular/common';
import localeFr from '@angular/common/locales/fr';

// Without registered locale data the Angular date pipe formats in en-US: the
// activity lists showed "Sat 17/09" and "Dec 24, 2026" in an otherwise French
// screen. Register French, then pick the pipe's locale from the language the
// admin actually chose.
registerLocaleData(localeFr);

/**
 * The date pipe's locale, read from the persisted choice so it is right on the
 * first paint — before LanguageService.initialize() has run. LOCALE_ID is
 * resolved once, so a live language toggle needs a reload for pipe-formatted
 * dates; moment-based dates (date.util) follow the switch immediately because
 * LanguageService also sets moment's locale.
 */
function localeIdFactory(): string {
  try {
    const stored = localStorage.getItem('smatchy:bo:lang');
    if (stored === 'en') return 'en-US';
    if (stored === 'fr') return 'fr';
  } catch {
    // Locked-down profiles throw on localStorage; fall through to the browser.
  }
  const browser = (navigator.language || '').slice(0, 2).toLowerCase();
  return browser === 'en' ? 'en-US' : 'fr';
}
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { RouterLink } from '@angular/router';
import { UiAuthModule, UiAuthService } from '@smatchy/ui-auth';
import { NotfoundComponent } from './presentations/components/notfound/notfound.component';
import { AppLayoutModule } from './presentations/layout/app.layout.module';
import { BASE_PATH, Configuration } from '@smatchy/sdk';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { environment } from '../environments/environment';
import { TokenInterceptor } from './token.interceptor';
import { BackofficeSessionService } from './backoffice-session.service';
import { BackofficeI18nModule } from './i18n/i18n.module';
import { LanguageService } from './i18n/language.service';

function apiInitializer() {
  return environment.api;
}

function sessionInitializer(sessionService: BackofficeSessionService) {
  return () => sessionService.initialize();
}

function languageInitializer(languageService: LanguageService) {
  return () => languageService.initialize();
}

@NgModule({
  declarations: [AppComponent, NotfoundComponent],
  imports: [
    AppRoutingModule,
    AppLayoutModule,
    BackofficeI18nModule,
    RouterLink,
    UiAuthModule.forRoot({
      apiSignInUrl: environment.api + '/auth/sign-up',
      loginRoute: '/login',
      keyStorage: 'smatchy:bo',
      apiProfileUrl: environment.api + '/api/v1/profile',
    }),
  ],
  providers: [
    // Routes uncaught Angular exceptions to Sentry. Initialised in main.ts.
    // No custom ErrorHandler existed before, so nothing is being replaced.
    { provide: ErrorHandler, useValue: Sentry.createErrorHandler() },
    { provide: LocationStrategy, useClass: PathLocationStrategy },
    { provide: LOCALE_ID, useFactory: localeIdFactory },
    {
      provide: Configuration,
      useFactory: (authService: UiAuthService) =>
        new Configuration({
          basePath: apiInitializer(),
          accessToken: authService.getAccessToken.bind(authService),
        }),
      deps: [UiAuthService],
      multi: false,
    },
    {
      provide: BASE_PATH,
      useFactory: apiInitializer,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true,
    },
    provideAppInitializer(() => sessionInitializer(inject(BackofficeSessionService))()),
    provideAppInitializer(() => languageInitializer(inject(LanguageService))()),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
